import { Container, Graphics } from 'pixi.js';
import { OFFICE_DOORS } from '@duck/contracts';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { registerScenery } from '../scenery';
import { mix, lighten, darken } from '../theme-art';

// ── EDITABLE SCENERY REGISTRATIONS (passive metadata for the level editor) ──────────────────────────
// Each bespoke server-room FURNITURE piece is registered under a stable kind so the editor can re-render
// / add it. The serverRack/coolingUnit builders take (art, mix, darken, lighten, …) directly, so we hand
// them the imported colour helpers + the SAME literal args the place() calls use. Each (units, seed) rack
// variant is a DISTINCT kind (server-rack-<units>u-<lane>, lane=seed) with the args baked into the lambda.
// Painted/architectural backdrop (raised floor, aisle marking, cable trays, patch panel, floor cable runs,
// cold glows) is NOT registered. NOTE: both builders draw at local origin (no ctx.cx/cy geometry use), so
// they're already origin-anchored — the editor positions the captured piece itself.
registerScenery('server-rack-5u-1', (art) => serverRack(art, mix, darken, lighten, 5, 1), 'Server rack', '🖥️');
registerScenery('server-rack-7u-2', (art) => serverRack(art, mix, darken, lighten, 7, 2), 'Server rack', '🖥️');
registerScenery('server-rack-5u-3', (art) => serverRack(art, mix, darken, lighten, 5, 3), 'Server rack', '🖥️');
registerScenery('server-rack-6u-5', (art) => serverRack(art, mix, darken, lighten, 6, 5), 'Server rack', '🖥️');
registerScenery('server-rack-4u-4', (art) => serverRack(art, mix, darken, lighten, 4, 4), 'Server rack', '🖥️');
registerScenery('server-rack-4u-6', (art) => serverRack(art, mix, darken, lighten, 4, 6), 'Server rack', '🖥️');
registerScenery('cooling-unit', (art) => coolingUnit(art, mix, darken, lighten), 'Cooling unit', '❄️');

/**
 * SERVER ROOM — the humming machine heart of the office.
 *
 * A moody, technical, atmospheric room: rows of server racks blinking quiet status LEDs down the cold
 * left wall, overhead cable trays snaking along the back, a chunky cooling / AC unit, and a single lit
 * ops workstation where someone keeps the world alive. Cooler and a touch darker than the rest of the
 * office — the only warmth is the green/blue pinpricks of running hardware.
 *
 * Geometry (zone rect x40..440, y580..840; interior 52..428 × 592..828, centre 240,710). The centre lane
 * x160..320 is the N↔S door corridor (both doors centred on the top/bottom edges) and is kept CLEAR so
 * ducks walk straight through lounge↔game-room. Racks line BOTH side walls: a west column (x~63..113) and
 * a mirrored east column (x~367..417), with cable trays / cooling / a monitoring desk filling the bands —
 * never crossing the central corridor or blocking either door.
 *
 * The CENTRE is no longer bare floor: a flat raised-floor grid (perforated data-centre panels) tiles the
 * whole interior, the corridor is painted as a hazard/footpath SERVICE AISLE (guide lines + chevrons, a
 * pure flat decal so the lane stays fully walkable), and a compact back-to-back rack ISLAND hugs each
 * flank of the corridor at the room's vertical centre (west x~132, east x~348 — both 3px clear of the
 * corridor edges) so the aisle reads as a real data-centre row with gear on both sides, not a void.
 *
 * Built ONCE on map build/re-skin (never per frame, never allocating in a loop). Everything themes off
 * `art` + the colour helpers so it re-skins per world, biased COOL (this is the server room).
 */

/** A cool, slightly desaturated darken of a theme tone — the server room's signature chilly steel. */
function steel(art: RoomContext['art'], mix: RoomContext['mix'], darken: RoomContext['darken'], t = 0.55): number {
  // Pull the wall toward a cold blue-grey, then darken: cabinets/racks read as cold metal under any theme.
  return darken(mix(art.wall, 0x39414f, 0.6), t);
}

/**
 * One server rack: a tall dark cabinet of stacked blade units, each with a quiet status LED + a thin
 * vent strip. `units` controls height; `seed` decorrelates the LED pattern so a row of racks isn't
 * identical. Static (no animation) — the "blink" is implied by the mix of lit/dim LEDs.
 */
function serverRack(
  art: RoomContext['art'],
  mix: RoomContext['mix'],
  darken: RoomContext['darken'],
  lighten: RoomContext['lighten'],
  units = 7,
  seed = 0,
): Container {
  const c = new Container();
  const body = steel(art, mix, darken, 0.62);
  const w = 50;
  const h = 24 + units * 17;
  const top = -h;
  c.addChild(new Graphics().ellipse(0, 6, w / 2 + 4, 9).fill({ color: 0x000000, alpha: 0.3 }));

  const g = new Graphics()
    // cabinet shell
    .roundRect(-w / 2, top, w, h, 5)
    .fill(body)
    // brushed top highlight + a darker right edge for a hint of form
    .roundRect(-w / 2, top, w, 6, 5)
    .fill({ color: 0xffffff, alpha: 0.08 })
    .rect(w / 2 - 5, top + 6, 5, h - 10)
    .fill({ color: 0x000000, alpha: 0.18 });
  c.addChild(g);

  // Blade units stacked up the rack: each a recessed dark slot with vent slits + a status LED pair.
  const ledOn = [0x5ad17a, 0x5ad1ff, art.accent, 0xffd23f]; // green / cyan / theme / amber
  for (let r = 0; r < units; r++) {
    const uy = top + 14 + r * 17;
    const blade = new Graphics()
      .roundRect(-w / 2 + 5, uy, w - 10, 13, 2)
      .fill(darken(body, 0.55))
      .roundRect(-w / 2 + 5, uy, w - 10, 2, 2)
      .fill({ color: 0xffffff, alpha: 0.05 });
    // vent slits across the blade face
    for (let s = 0; s < 5; s++) {
      blade.rect(-w / 2 + 22 + s * 4, uy + 4, 1.5, 6).fill({ color: 0x000000, alpha: 0.4 });
    }
    // two status LEDs on the left of each blade — mostly lit, a few dim (a believable running rack)
    const lit = (r * 7 + seed * 3) % 5 !== 0;
    const lit2 = (r * 5 + seed) % 4 !== 0;
    const cA = ledOn[(r + seed) % ledOn.length]!;
    const cB = ledOn[(r + seed + 2) % ledOn.length]!;
    blade
      .circle(-w / 2 + 11, uy + 6.5, 1.7)
      .fill(lit ? { color: cA, alpha: 0.95 } : { color: darken(cA, 0.6), alpha: 0.5 })
      .circle(-w / 2 + 16, uy + 6.5, 1.7)
      .fill(lit2 ? { color: cB, alpha: 0.9 } : { color: darken(cB, 0.6), alpha: 0.5 });
    // a soft glow halo on the brightest LED so the room reads as "lit by its hardware"
    if (lit) blade.circle(-w / 2 + 11, uy + 6.5, 4).fill({ color: lighten(cA, 0.2), alpha: 0.18 });
    c.addChild(blade);
  }
  return c;
}

/**
 * A wall-mounted cooling / AC unit: a louvred housing with a cold blue intake glow + two fan discs.
 * Reads as the thing keeping the racks alive. Drawn shallow so it hugs the wall.
 */
function coolingUnit(
  art: RoomContext['art'],
  mix: RoomContext['mix'],
  darken: RoomContext['darken'],
  lighten: RoomContext['lighten'],
): Container {
  const c = new Container();
  const shell = steel(art, mix, darken, 0.45);
  const w = 78;
  const h = 40;
  c.addChild(new Graphics().ellipse(0, h / 2 + 4, w / 2, 7).fill({ color: 0x000000, alpha: 0.25 }));
  const g = new Graphics()
    .roundRect(-w / 2, -h / 2, w, h, 5)
    .fill(shell)
    .roundRect(-w / 2, -h / 2, w, 5, 5)
    .fill({ color: 0xffffff, alpha: 0.1 })
    // louvre slats across the housing
    ;
  for (let i = 0; i < 6; i++) {
    g.roundRect(-w / 2 + 6, -h / 2 + 8 + i * 4.5, w - 12, 2.5, 1).fill({ color: 0x000000, alpha: 0.22 });
  }
  c.addChild(g);
  // two recessed fan discs with a cold blue intake glow
  const cold = mix(0x4fb4ff, art.accent, 0.25);
  for (const fx of [-18, 18]) {
    c.addChild(
      new Graphics()
        .circle(fx, 4, 11)
        .fill(darken(shell, 0.45))
        .circle(fx, 4, 11)
        .stroke({ color: lighten(cold, 0.1), width: 1.5, alpha: 0.5 })
        .circle(fx, 4, 6)
        .fill({ color: cold, alpha: 0.35 })
        // four fan blades hinted as spokes
        .moveTo(fx, 4)
        .lineTo(fx, -5)
        .moveTo(fx, 4)
        .lineTo(fx + 8, 7)
        .moveTo(fx, 4)
        .lineTo(fx - 8, 7)
        .moveTo(fx, 4)
        .lineTo(fx, 12)
        .stroke({ color: darken(shell, 0.2), width: 1.5, alpha: 0.7 })
        .circle(fx, 4, 2)
        .fill(lighten(cold, 0.3)),
    );
  }
  return c;
}

/**
 * Overhead cable tray run: a ladder of rails with bundled cables draped along it. Drawn near the back
 * wall as flat top-down infrastructure (no contact shadow — it's "up high"). `len` sets the run length.
 */
function cableTray(
  art: RoomContext['art'],
  mix: RoomContext['mix'],
  darken: RoomContext['darken'],
  lighten: RoomContext['lighten'],
  len: number,
): Container {
  const c = new Container();
  const rail = steel(art, mix, darken, 0.4);
  const g = new Graphics()
    // two side rails
    .rect(-len / 2, -5, len, 2.5)
    .fill(rail)
    .rect(-len / 2, 5, len, 2.5)
    .fill(rail);
  // rungs
  for (let x = -len / 2 + 6; x < len / 2; x += 14) {
    g.rect(x, -5, 2, 12).fill(darken(rail, 0.2));
  }
  c.addChild(g);
  // bundled cables draped along the tray (a few coloured strands)
  const cables = [mix(art.accent, 0x000000, 0.2), 0x4a5366, 0x6b5a3a];
  cables.forEach((col, i) => {
    const yy = -2 + i * 2;
    c.addChild(
      new Graphics()
        .moveTo(-len / 2 + 2, yy)
        .bezierCurveTo(-len / 6, yy + 4, len / 6, yy - 3, len / 2 - 2, yy + 1)
        .stroke({ color: col, width: 2.5, alpha: 0.85 }),
    );
  });
  // a faint cyan data-light bleed so the back wall glows cold
  c.addChild(new Graphics().rect(-len / 2, 8, len, 2).fill({ color: lighten(mix(0x4fb4ff, art.accent, 0.3), 0.1), alpha: 0.12 }));
  return c;
}

/** A small wall-mounted patch / status panel: a dark plate with a grid of tiny port LEDs. */
function patchPanel(art: RoomContext['art'], mix: RoomContext['mix'], darken: RoomContext['darken']): Container {
  const c = new Container();
  const plate = steel(art, mix, darken, 0.55);
  const g = new Graphics().roundRect(-26, -14, 52, 28, 3).fill(plate).roundRect(-26, -14, 52, 3, 3).fill({ color: 0xffffff, alpha: 0.08 });
  const ports = [0x5ad17a, 0x5ad1ff, art.accent, 0x4a5366];
  for (let row = 0; row < 2; row++) {
    for (let i = 0; i < 8; i++) {
      const on = (row * 8 + i * 3) % 5 !== 0;
      const col = ports[(row + i) % ports.length]!;
      g.rect(-22 + i * 5.5, -8 + row * 11, 4, 6)
        .fill(darken(plate, 0.5))
        .circle(-20 + i * 5.5, -6 + row * 11, 1.1)
        .fill(on ? { color: col, alpha: 0.9 } : { color: darken(col, 0.6), alpha: 0.5 });
    }
  }
  c.addChild(g);
  return c;
}

/** A soft cold floor-glow pool — the room lit by its own cold hardware light, not warm lamps. */
function coldGlow(art: RoomContext['art'], mix: RoomContext['mix'], lighten: RoomContext['lighten'], rx: number, ry: number, tint = 0x4fb4ff): Graphics {
  return new Graphics().ellipse(0, 0, rx, ry).fill({ color: lighten(mix(tint, art.accent, 0.25), 0.05), alpha: 0.1 });
}

/**
 * RAISED-FLOOR GRID — a flat, top-down tiling of perforated data-centre floor panels drawn across the
 * whole interior so the centre reads as a real raised floor, not bare dark void. Each `tile`-sized panel
 * is a faint seam rectangle with a couple of cooling-perforation dots; a few panels get a hairline cold
 * accent so the grid catches the hardware light. Pure flat decal (no contact shadow, no body) — it lives
 * under everything and never blocks a walk. Origin is the panel grid's top-left; caller positions it.
 */
function raisedFloor(
  art: RoomContext['art'],
  mix: RoomContext['mix'],
  lighten: RoomContext['lighten'],
  darken: RoomContext['darken'],
  w: number,
  h: number,
  tile = 32,
): Container {
  const c = new Container();
  const seam = lighten(mix(art.wall, 0x39414f, 0.6), 0.12);
  const perf = darken(mix(art.wall, 0x39414f, 0.6), 0.35);
  const cols = Math.floor(w / tile);
  const rows = Math.floor(h / tile);
  const g = new Graphics();
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const px = col * tile;
      const py = r * tile;
      // panel seam (thin inset rect) — the join between two raised-floor tiles
      g.roundRect(px + 1, py + 1, tile - 2, tile - 2, 2).stroke({ color: seam, width: 1, alpha: 0.13 });
      // four cooling-perforation dots clustered in the panel centre
      for (const [dx, dy] of [
        [-4, -4],
        [4, -4],
        [-4, 4],
        [4, 4],
      ] as const) {
        g.circle(px + tile / 2 + dx, py + tile / 2 + dy, 1.1).fill({ color: perf, alpha: 0.4 });
      }
      // a sparse scatter of cold-lit panels so the floor isn't a dead-flat repeat
      if ((r * 3 + col * 7) % 11 === 0) {
        g.roundRect(px + 3, py + 3, tile - 6, tile - 6, 2).fill({ color: lighten(mix(0x4fb4ff, art.accent, 0.3), 0.1), alpha: 0.05 });
      }
    }
  }
  c.addChild(g);
  return c;
}

/**
 * CORRIDOR AISLE MARKING — a flat hazard/footpath decal painted down the central N↔S walkway so the
 * corridor reads as a purposeful data-centre service aisle (where techs walk), not an accidental gap.
 * Two dashed cold-amber guide lines flank a faint walkway wash, with chevron direction hints. Drawn
 * flat under the ducks; it occupies the corridor but is purely visual (a floor decal, never a body), so
 * the lane stays fully walkable. Centred on the caller's (x,y); `len` runs along the N↔S axis.
 */
function aisleMarking(
  art: RoomContext['art'],
  mix: RoomContext['mix'],
  lighten: RoomContext['lighten'],
  laneW: number,
  len: number,
): Container {
  const c = new Container();
  const guide = lighten(mix(0xffd23f, art.accent, 0.2), 0.05); // cold-amber hazard line
  const g = new Graphics();
  // faint walkway wash so the lane floor lifts a touch from the surrounding raised floor
  g.roundRect(-laneW / 2, -len / 2, laneW, len, 6).fill({ color: lighten(mix(0x4fb4ff, art.accent, 0.3), 0.08), alpha: 0.04 });
  // two dashed guide lines flanking the lane
  const dash = 16;
  const gap = 12;
  for (const ex of [-laneW / 2 + 10, laneW / 2 - 10]) {
    for (let y = -len / 2 + 8; y < len / 2 - 8; y += dash + gap) {
      g.roundRect(ex - 2, y, 4, dash, 2).fill({ color: guide, alpha: 0.22 });
    }
  }
  // a column of faint chevrons hinting the through-route, fading toward centre
  for (let y = -len / 2 + 26; y < len / 2 - 26; y += 52) {
    g.moveTo(-12, y).lineTo(0, y + 9).lineTo(12, y).stroke({ color: guide, width: 2, alpha: 0.12 });
  }
  c.addChild(g);
  return c;
}

/**
 * FLAT FLOOR CABLE RUN — a short bundle of data cables snaking flat across the raised floor between two
 * pieces of gear (e.g. a rack island toward the side racks). A few coloured strands plus a faint cyan
 * data-light bleed, no rails (it's at floor level, not an overhead tray). Drawn from -len/2..len/2 on X.
 */
function floorCableRun(art: RoomContext['art'], mix: RoomContext['mix'], lighten: RoomContext['lighten'], len: number): Container {
  const c = new Container();
  const cables = [mix(art.accent, 0x000000, 0.2), 0x4a5366, 0x6b5a3a, mix(0x4fb4ff, art.accent, 0.3)];
  cables.forEach((col, i) => {
    const yy = -3 + i * 2;
    c.addChild(
      new Graphics()
        .moveTo(-len / 2, yy)
        .bezierCurveTo(-len / 6, yy + 5, len / 6, yy - 4, len / 2, yy + 2)
        .stroke({ color: col, width: 2, alpha: 0.7 }),
    );
  });
  // a faint cold data-light bleed along the bundle so it catches the hardware glow
  c.addChild(new Graphics().rect(-len / 2, 4, len, 2).fill({ color: lighten(mix(0x4fb4ff, art.accent, 0.3), 0.1), alpha: 0.1 }));
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { zone, art, f, mix, lighten, darken, cx } = ctx;
  const left = zone.x; // 40
  const right = zone.x + zone.width; // 440
  const top = zone.y; // 580
  const bottom = zone.y + zone.height; // 840

  // The N (top) + S (bottom) doors both sit at pos=240, width=160 → both openings span x[160..320].
  // That makes the centre column a straight N↔S walking corridor (lounge↔game-room). We derive the
  // widest door span from the contract (so this rides any future door move) and keep ALL furniture out
  // of it: a WEST band (x<corridorL) and a mirrored EAST band (x>corridorR), side walls only.
  const sideDoors = OFFICE_DOORS.filter((d) => d.zoneId === zone.id && (d.side === 'N' || d.side === 'S'));
  const corridorL = Math.min(...sideDoors.map((d) => d.pos - d.width / 2)); // ~160
  const corridorR = Math.max(...sideDoors.map((d) => d.pos + d.width / 2)); // ~320

  // ── Ambient cold floor light: a faint blue wash so the whole room feels cooler/darker than the office.
  place(ctx, coldGlow(art, mix, lighten, 170, 110, 0x3a5a8a), cx, (top + bottom) / 2);

  // ── RAISED FLOOR: tile the WHOLE interior with perforated data-centre floor panels so the centre reads
  // as a real raised floor, never bare void. Pure flat decal under everything; inset a little off the inner
  // walls (x52..428, y592..828). Positioned by its top-left, so anchor at (left+12, top+12).
  const floorW = right - left - 24; // ~376 — inner span minus margins
  const floorH = bottom - top - 24; // ~236
  place(ctx, raisedFloor(art, mix, lighten, darken, floorW, floorH, 32), left + 12, top + 12);

  // ── CORRIDOR AISLE: paint the central N↔S walkway as a purposeful service aisle (hazard guide lines +
  // walkway wash + chevrons). It occupies x[160..320] but is a FLAT DECAL ONLY — no body — so the lane
  // stays fully walkable lounge↔game-room. Centred on the room's vertical axis, running the full height.
  place(ctx, aisleMarking(art, mix, lighten, corridorR - corridorL, bottom - top - 28), cx, (top + bottom) / 2);

  // ── WEST WALL: a humming column of two server racks (back + mid), leaving the south-west corner for the
  // monitoring station. Rack is 50 wide; centred at x≈88 → span x[63..113], a clean margin off the west
  // wall (inner face x52) and well left of the x160 corridor edge. A green hardware-glow pool sits at each
  // rack's base. The taller room (260) lets each rack breathe down the wall.
  const westX = left + 48; // ~88
  const westRacks: Array<{ y: number; units: number; seed: number }> = [
    { y: top + 116, units: 5, seed: 1 }, // back rack (shorter — its top sits right at the back wall)
    { y: top + 178, units: 7, seed: 2 }, // mid rack
  ];
  for (const { y, units, seed } of westRacks) {
    place(ctx, coldGlow(art, mix, lighten, 40, 22, 0x5ad17a), westX, y + 4); // green hardware glow at its base
    place(ctx, serverRack(art, mix, darken, lighten, units, seed), westX, y, `server-rack-${units}u-${seed}`);
  }

  // ── EAST WALL: a mirrored column of two racks (back + front), so both side walls hum with hardware
  // (the design intent), with the cooling unit tucked between them. Rack centred at x≈392 → span
  // x[367..417], a clean margin off the east wall (inner face x428) and right of the x320 corridor edge.
  const eastX = right - 48; // ~392
  const eastRacks: Array<{ y: number; units: number; seed: number }> = [
    { y: top + 116, units: 5, seed: 3 }, // back rack (pairs the west back rack)
    { y: top + 196, units: 6, seed: 5 }, // front rack (base lifts clear of the corner cabinet below)
  ];
  for (const { y, units, seed } of eastRacks) {
    place(ctx, coldGlow(art, mix, lighten, 40, 22, 0x5ad17a), eastX, y + 4);
    place(ctx, serverRack(art, mix, darken, lighten, units, seed), eastX, y, `server-rack-${units}u-${seed}`);
  }

  // ── CENTRE AISLE ISLANDS: a compact back-to-back rack island hugging EACH flank of the corridor at the
  // room's vertical CENTRE — the exact void — so the data-centre aisle has gear on both sides (techs walk
  // the lane between them) instead of bare dark floor. Each rack is 50 wide; the WEST island centres at
  // x≈132 → span x[107..157] (3px clear of the x160 corridor edge) and the EAST island at x≈348 → span
  // x[323..373] (3px clear of the x320 edge), so NEITHER touches the corridor. They read as the inner ends
  // of the side-wall rack banks (a 4-unit island is short enough to clear the mid-wall cooling unit below
  // and the front-band ops desk / cabinet / plant). A flat floor cable run links each island to its wall.
  const aisleY = top + 128; // ~708 — room vertical centre, in the heart of the former void
  const westIslandX = corridorL - 28; // ~132 — corridor-safe, hugs the lane's west flank
  const eastIslandX = corridorR + 28; // ~348 — corridor-safe, hugs the lane's east flank
  for (const ix of [westIslandX, eastIslandX]) {
    place(ctx, coldGlow(art, mix, lighten, 38, 20, 0x5ad17a), ix, aisleY + 4); // green hardware glow at the base
    const islandSeed = ix === westIslandX ? 4 : 6;
    place(ctx, serverRack(art, mix, darken, lighten, 4, islandSeed), ix, aisleY, `server-rack-4u-${islandSeed}`);
  }
  // floor cable runs spilling from each island out toward its side wall (flat decals, no body to block).
  place(ctx, floorCableRun(art, mix, lighten, 34), (westIslandX + westX) / 2, aisleY + 16);
  place(ctx, floorCableRun(art, mix, lighten, 34), (eastIslandX + eastX) / 2, aisleY + 16);

  // ── BACK WALL: overhead cable trays running along the top, each clamped to STOP at the N-door corridor
  // edge (so neither tray ever crosses the x[corridorL..corridorR] gap). West run hugs the west racks;
  // east run hugs the east gear. Both sit "up high", below the top wall band.
  const westTrayW = corridorL - (left + 18) - 4; // from wall margin to just shy of the corridor
  const eastTrayW = right - 18 - corridorR - 4; // from corridor to just shy of the east wall
  place(ctx, cableTray(art, mix, darken, lighten, westTrayW), (left + 18 + corridorL) / 2 - 2, top + 26);
  place(ctx, cableTray(art, mix, darken, lighten, eastTrayW), (corridorR + right - 18) / 2 + 2, top + 26);
  // A wall patch/status panel high on the back-east wall, just inside the corridor edge (52 wide → span
  // x[324..376], clear of the corridor and the cable tray above it).
  place(ctx, patchPanel(art, mix, darken), corridorR + 30, top + 32);

  // ── EAST WALL (mid): the cooling / AC unit keeping the racks alive, tucked between the east racks and
  // hugging the wall. 78 wide; centre x≈383 → span x[344..422], inside the east band (inner face x428).
  const coolX = right - 57; // ~383 — keeps the 78-wide housing's right edge inside the wall
  const coolY = top + 158; // ~738 — midway between the back and front east racks
  place(ctx, coldGlow(art, mix, lighten, 60, 30, 0x4fb4ff), coolX, coolY + 4);
  place(ctx, coolingUnit(art, mix, darken, lighten), coolX, coolY, 'cooling-unit');

  // ── WEST WALL (south-west corner): the single lit monitoring desk, swivel chair, monitor + tower.
  // Faces the corridor so the operator watches the floor; the cold screen glow is the one bit of
  // "someone is here" warmth amid the machines. 72-wide desk centred at x≈96 → span x[60..132], inside
  // the west band below the mid rack, clear of the x160 corridor and the S-door opening.
  const opsX = left + 56; // ~96 — keeps the desk's contact shadow inside the west wall margin
  const opsY = bottom - 62; // ~778 — corner station; chair (to y≈822) clears the south wall
  place(ctx, coldGlow(art, mix, lighten, 46, 24, 0x5ad1ff), opsX, opsY + 6); // floor screen-glow (under, drawn first)
  place(ctx, f.longDesk(art, 72), opsX, opsY);
  place(ctx, f.computer(art), opsX - 8, opsY - 8); // monitor + tower, screen glow on the desk
  place(ctx, f.officeChair(art), opsX, opsY + 28); // operator seat, pulled out in front of the desk

  // ── EAST WALL (south-east corner): a low storage cabinet of spares (drives/cables), balancing the ops
  // desk diagonally. 68 wide; centre x≈383 → span x[349..417], inside the east band.
  place(ctx, f.cabinet(art), right - 57, bottom - 34); // ~383 / ~806, south-east corner

  // ── A tall plant in the south-east, between the front east rack and the corridor — the single sliver
  // of life amid the cold steel. Tiny footprint (~14 wide) at x≈345, clear of the corridor and S door.
  place(ctx, f.plant(art, 'tall'), corridorR + 25, bottom - 64); // ~345 / ~776
};

export default decorate;
